import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { 
  Settings, Store, Save, RefreshCw, AlertTriangle, CheckCircle2,
  Banknote, CreditCard, Info, Plus, Pencil, Trash2, ArrowRight, Search, Loader2, ToggleLeft, ToggleRight, History
} from 'lucide-react';
import SageProductSelector from '@/components/retail/SageProductSelector';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('pt-PT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value);
};

// Effective value display with source indicator
const EffectiveValue = ({ value, isOverride, label }) => (
  <div className="flex items-center gap-2">
    <span className="font-medium">{formatCurrency(value)}</span>
    {isOverride && (
      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Override</span>
    )}
    {!isOverride && (
      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Global</span>
    )}
  </div>
);

export default function RetailConfigAdmin() {
  const { getStoreName } = useStoreMapping();
  const { user } = useContext(AuthContext);
  
  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Global config
  const [globalConfig, setGlobalConfig] = useState({
    cash_tolerance: 0,
    card_tolerance: 0,
    default_cash_float: 0,
  });
  
  // Store configs
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [storeConfig, setStoreConfig] = useState(null);
  const [storeConfigForm, setStoreConfigForm] = useState({
    cash_float_retained: '',
    cash_tolerance: '',
    card_tolerance: '',
    notes: '',
  });
  
  // WinTouch/SAGE mapping
  const [storeMapping, setStoreMapping] = useState([]);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [mappingForm, setMappingForm] = useState({
    name: '',
    wintouch_store_code: '',
    sage_warehouse: '',
    isl_series: '',
    active: true,
  });
  
  // SKU Mappings state (WinTouch SKU → SAGE SKU)
  const [skuMappings, setSkuMappings] = useState([]);
  const [loadingSkuMappings, setLoadingSkuMappings] = useState(false);
  const [showSkuMappingDialog, setShowSkuMappingDialog] = useState(false);
  const [editingSkuMapping, setEditingSkuMapping] = useState(null);
  const [skuMappingForm, setSkuMappingForm] = useState({
    wintouch_sku: '',
    sage_sku: '',
    description: '',
  });
  const [savingSkuMapping, setSavingSkuMapping] = useState(false);
  const [skuMappingError, setSkuMappingError] = useState(null);
  // Product search for autocomplete
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Load global config
  useEffect(() => {
    const loadGlobalConfig = async () => {
      try {
        const res = await axios.get(`${API}/retail-closures/config/global`);
        setGlobalConfig({
          cash_tolerance: res.data.cash_tolerance || 0,
          card_tolerance: res.data.card_tolerance || 0,
          default_cash_float: res.data.default_cash_float || 0,
        });
      } catch (err) {
        console.error('Error loading global config:', err);
      }
    };
    loadGlobalConfig();
  }, []);
  
  // Load stores
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        setStores(res.data.stores || []);
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    };
    loadStores();
  }, []);
  
  // Load WinTouch/SAGE mapping
  useEffect(() => {
    if (user?.role === 'admin') {
      loadMappings();
    }
  }, [user]);
  
  // Load store config when selected
  useEffect(() => {
    if (!selectedStore) {
      setStoreConfig(null);
      setStoreConfigForm({ cash_float_retained: '', cash_tolerance: '', card_tolerance: '', notes: '' });
      return;
    }
    
    const loadStoreConfig = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/retail-closures/config/store/${selectedStore}`);
        setStoreConfig(res.data);
        
        // Check if values are overrides or global defaults
        const hasFloatOverride = res.data.cash_float_retained !== globalConfig.default_cash_float;
        const hasCashTolOverride = res.data.cash_tolerance !== globalConfig.cash_tolerance;
        const hasCardTolOverride = res.data.card_tolerance !== globalConfig.card_tolerance;
        
        setStoreConfigForm({
          cash_float_retained: hasFloatOverride ? res.data.cash_float_retained.toString() : '',
          cash_tolerance: hasCashTolOverride ? res.data.cash_tolerance.toString() : '',
          card_tolerance: hasCardTolOverride ? res.data.card_tolerance.toString() : '',
          notes: res.data.notes || '',
        });
      } catch (err) {
        console.error('Error loading store config:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStoreConfig();
  }, [selectedStore, globalConfig]);
  
  // Save global config
  const handleSaveGlobal = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      await axios.put(`${API}/retail-closures/config/global`, globalConfig);
      setSuccess('Configuração global guardada com sucesso');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configuração global');
    } finally {
      setSaving(false);
    }
  };
  
  // Save store config
  const handleSaveStore = async () => {
    if (!selectedStore) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const payload = {
        store_code: selectedStore,
      };
      
      // Only include overrides (non-empty values)
      if (storeConfigForm.cash_float_retained !== '') {
        payload.cash_float_retained = parseFloat(storeConfigForm.cash_float_retained);
      }
      if (storeConfigForm.cash_tolerance !== '') {
        payload.cash_tolerance = parseFloat(storeConfigForm.cash_tolerance);
      }
      if (storeConfigForm.card_tolerance !== '') {
        payload.card_tolerance = parseFloat(storeConfigForm.card_tolerance);
      }
      if (storeConfigForm.notes) {
        payload.notes = storeConfigForm.notes;
      }
      
      await axios.put(`${API}/retail-closures/config/store/${selectedStore}`, payload);
      setSuccess(`Configuração de ${selectedStore} guardada com sucesso`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Reload store config
      const res = await axios.get(`${API}/retail-closures/config/store/${selectedStore}`);
      setStoreConfig(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configuração da loja');
    } finally {
      setSaving(false);
    }
  };
  
  // Clear store override
  const handleClearOverride = (field) => {
    setStoreConfigForm(prev => ({
      ...prev,
      [field]: '',
    }));
  };
  
  // Load WinTouch/SAGE mappings
  const loadMappings = async () => {
    setLoadingMapping(true);
    try {
      const res = await axios.get(`${API}/retail-closures/admin/wintouch-stores`);
      setStoreMapping(res.data.stores || []);
    } catch (err) {
      console.error('Error loading store mapping:', err);
      setStoreMapping([]);
    } finally {
      setLoadingMapping(false);
    }
  };
  
  // Handle edit mapping
  const handleEditMapping = (store) => {
    setMappingForm({
      name: store.name || '',
      wintouch_store_code: store.wintouch_store_code,
      sage_warehouse: store.sage_warehouse || '',
      isl_series: store.isl_series || '',
      active: store.active ?? true,
    });
    setEditingStore(store.wintouch_store_code);
    setShowAddDialog(true);
  };
  
  // Handle save mapping (create or update)
  const handleSaveMapping = async () => {
    if (!mappingForm.wintouch_store_code || !mappingForm.sage_warehouse) {
      setError('Código WinTouch e Armazém SAGE são obrigatórios');
      return;
    }
    
    setSavingMapping(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        name: mappingForm.name,
        sage_warehouse: mappingForm.sage_warehouse,
        isl_series: mappingForm.isl_series || '',
        active: mappingForm.active.toString(),
      });
      
      if (editingStore) {
        // Update existing
        await axios.put(
          `${API}/retail-closures/admin/wintouch-stores/${editingStore}?${params.toString()}`
        );
        setSuccess(`Mapeamento ${editingStore} atualizado com sucesso`);
      } else {
        // Create new
        params.append('wintouch_store_code', mappingForm.wintouch_store_code);
        await axios.post(
          `${API}/retail-closures/admin/wintouch-stores?${params.toString()}`
        );
        setSuccess(`Mapeamento ${mappingForm.wintouch_store_code} criado com sucesso`);
      }
      
      setShowAddDialog(false);
      setEditingStore(null);
      await loadMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar mapeamento');
    } finally {
      setSavingMapping(false);
    }
  };
  
  // Handle delete mapping
  const handleDeleteMapping = async (code) => {
    if (!window.confirm(`Tem a certeza que deseja apagar o mapeamento "${code}"?`)) {
      return;
    }
    
    setError(null);
    try {
      await axios.delete(`${API}/retail-closures/admin/wintouch-stores/${code}`);
      setSuccess(`Mapeamento ${code} apagado com sucesso`);
      await loadMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao apagar mapeamento');
    }
  };
  
  // ============================================================================
  // SKU MAPPINGS FUNCTIONS (WinTouch SKU → SAGE SKU)
  // ============================================================================
  
  // Load SKU mappings
  const loadSkuMappings = async () => {
    setLoadingSkuMappings(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/sku-exceptions/mappings/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { active_only: false },
      });
      setSkuMappings(res.data.mappings || []);
    } catch (err) {
      console.error('Error loading SKU mappings:', err);
      setSkuMappingError('Erro ao carregar mapeamentos SKU');
    } finally {
      setLoadingSkuMappings(false);
    }
  };
  
  // Load SKU mappings on mount
  useEffect(() => {
    loadSkuMappings();
  }, []);
  
  // Search products for autocomplete
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/products`, {
        params: { search: query, limit: 15 },
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns 'items' not 'products'
      setProductSearchResults(res.data.items || res.data.products || []);
    } catch (err) {
      console.error('Error searching products:', err);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };
  
  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);
  
  // Handle product selection from autocomplete
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSkuMappingForm(prev => ({
      ...prev,
      sage_sku: product.sku,
    }));
    setProductSearch('');
    setProductSearchResults([]);
  };
  
  // Open dialog for new mapping
  const handleNewSkuMapping = () => {
    setEditingSkuMapping(null);
    setSkuMappingForm({ wintouch_sku: '', sage_sku: '', description: '' });
    setSelectedProduct(null);
    setProductSearch('');
    setSkuMappingError(null);
    setShowSkuMappingDialog(true);
  };
  
  // Open dialog for edit
  const handleEditSkuMapping = (mapping) => {
    setEditingSkuMapping(mapping);
    setSkuMappingForm({
      wintouch_sku: mapping.wintouch_sku,
      sage_sku: mapping.sage_sku,
      description: mapping.description || '',
    });
    setSelectedProduct(null);
    setProductSearch('');
    setSkuMappingError(null);
    setShowSkuMappingDialog(true);
  };
  
  // Save SKU mapping (create or update)
  const handleSaveSkuMapping = async () => {
    if (!skuMappingForm.wintouch_sku || !skuMappingForm.sage_sku) {
      setSkuMappingError('SKU WinTouch e SKU SAGE são obrigatórios');
      return;
    }
    
    setSavingSkuMapping(true);
    setSkuMappingError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingSkuMapping) {
        await axios.put(
          `${API}/sku-exceptions/mappings/${encodeURIComponent(skuMappingForm.wintouch_sku)}`,
          {
            sage_sku: skuMappingForm.sage_sku,
            description: skuMappingForm.description || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Mapeamento SKU atualizado com sucesso');
      } else {
        await axios.post(
          `${API}/sku-exceptions/mappings`,
          {
            wintouch_sku: skuMappingForm.wintouch_sku,
            sage_sku: skuMappingForm.sage_sku,
            description: skuMappingForm.description || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Mapeamento SKU criado com sucesso');
      }
      
      setShowSkuMappingDialog(false);
      await loadSkuMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setSkuMappingError(err.response?.data?.detail || 'Erro ao guardar mapeamento SKU');
    } finally {
      setSavingSkuMapping(false);
    }
  };
  
  // Deactivate mapping
  const handleDeactivateSkuMapping = async (wintouch_sku) => {
    if (!window.confirm(`Desativar mapeamento para ${wintouch_sku}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/sku-exceptions/mappings/${encodeURIComponent(wintouch_sku)}/deactivate`,
        { reason: 'Desativado manualmente via UI' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Mapeamento ${wintouch_sku} desativado`);
      await loadSkuMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao desativar mapeamento');
    }
  };
  
  // Reactivate mapping
  const handleReactivateSkuMapping = async (wintouch_sku) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/sku-exceptions/mappings/${encodeURIComponent(wintouch_sku)}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Mapeamento ${wintouch_sku} reativado`);
      await loadSkuMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao reativar mapeamento');
    }
  };
  
  // Check if user is admin
  if (user?.role !== 'admin' && user?.role !== 'procurement_manager') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Acesso Negado</AlertTitle>
          <AlertDescription>
            Esta área é reservada a administradores.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="config-admin-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuração de Fecho de Caixa</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configurar tolerâncias e fundos de caixa por loja
        </p>
      </div>
      
      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="bg-emerald-50 border-emerald-200 text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue="global" className="w-full">
        <TabsList>
          <TabsTrigger value="global">Configuração Global</TabsTrigger>
          <TabsTrigger value="stores">Por Loja</TabsTrigger>
          <TabsTrigger value="mapping">Mapeamento WinTouch/SAGE</TabsTrigger>
          <TabsTrigger value="sku-mappings">Mapeamentos SKU</TabsTrigger>
        </TabsList>
        
        {/* Global Config Tab */}
        <TabsContent value="global" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Valores por Defeito
              </CardTitle>
              <CardDescription>
                Estes valores aplicam-se a todas as lojas que não tenham configuração específica.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="global-cash-float" className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-emerald-600" />
                    Fundo de Caixa (Default)
                  </Label>
                  <Input
                    id="global-cash-float"
                    type="number"
                    step="0.01"
                    min="0"
                    value={globalConfig.default_cash_float}
                    onChange={(e) => setGlobalConfig(prev => ({ ...prev, default_cash_float: parseFloat(e.target.value) || 0 }))}
                    className="mt-1"
                    data-testid="global-cash-float"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Valor retido em caixa para troco
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="global-cash-tol" className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-amber-600" />
                    Tolerância Numerário
                  </Label>
                  <Input
                    id="global-cash-tol"
                    type="number"
                    step="0.01"
                    min="0"
                    value={globalConfig.cash_tolerance}
                    onChange={(e) => setGlobalConfig(prev => ({ ...prev, cash_tolerance: parseFloat(e.target.value) || 0 }))}
                    className="mt-1"
                    data-testid="global-cash-tolerance"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Diferença aceitável para CLOSED_OK
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="global-card-tol" className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    Tolerância Multibanco
                  </Label>
                  <Input
                    id="global-card-tol"
                    type="number"
                    step="0.01"
                    min="0"
                    value={globalConfig.card_tolerance}
                    onChange={(e) => setGlobalConfig(prev => ({ ...prev, card_tolerance: parseFloat(e.target.value) || 0 }))}
                    className="mt-1"
                    data-testid="global-card-tolerance"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Diferença aceitável para CLOSED_OK
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSaveGlobal} disabled={saving} data-testid="save-global-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'A guardar...' : 'Guardar Configuração Global'}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Info box */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Como funciona</AlertTitle>
            <AlertDescription className="text-blue-700">
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li><strong>Fundo de Caixa:</strong> Valor que fica em caixa após depósito (não é depositado)</li>
                <li><strong>Tolerância Numerário:</strong> Se |diferença| ≤ tolerância → CLOSED_OK</li>
                <li><strong>Tolerância Multibanco:</strong> Se |diferença| ≤ tolerância → CLOSED_OK</li>
                <li>Lojas podem ter valores específicos que sobrepõem estes defaults</li>
              </ul>
            </AlertDescription>
          </Alert>
        </TabsContent>
        
        {/* Store Config Tab */}
        <TabsContent value="stores" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Store className="w-5 h-5" />
                Configuração por Loja
              </CardTitle>
              <CardDescription>
                Configure valores específicos para cada loja. Campos vazios usam o valor global.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Store selector */}
              <div>
                <Label>Selecionar Loja</Label>
                <Select value={selectedStore} onValueChange={setSelectedStore}>
                  <SelectTrigger className="w-full md:w-[300px]" data-testid="store-selector">
                    <SelectValue placeholder="Escolha uma loja..." />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store} value={store}>{getStoreName(store)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              )}
              
              {selectedStore && !loading && storeConfig && (
                <>
                  {/* Effective values summary */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Valores Efetivos para {getStoreName(selectedStore)}</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Fundo de Caixa Efetivo:</span>
                        <EffectiveValue 
                          value={storeConfig.cash_float_retained} 
                          isOverride={storeConfigForm.cash_float_retained !== ''}
                        />
                      </div>
                      <div>
                        <span className="text-slate-500">Tolerância Numerário Efetiva:</span>
                        <EffectiveValue 
                          value={storeConfig.cash_tolerance} 
                          isOverride={storeConfigForm.cash_tolerance !== ''}
                        />
                      </div>
                      <div>
                        <span className="text-slate-500">Tolerância Multibanco Efetiva:</span>
                        <EffectiveValue 
                          value={storeConfig.card_tolerance} 
                          isOverride={storeConfigForm.card_tolerance !== ''}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Override form */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Banknote className="w-4 h-4 text-emerald-600" />
                          Fundo de Caixa
                        </span>
                        {storeConfigForm.cash_float_retained !== '' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => handleClearOverride('cash_float_retained')}
                          >
                            Usar Global
                          </Button>
                        )}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={`Global: ${formatCurrency(globalConfig.default_cash_float)}`}
                        value={storeConfigForm.cash_float_retained}
                        onChange={(e) => setStoreConfigForm(prev => ({ ...prev, cash_float_retained: e.target.value }))}
                        className="mt-1"
                        data-testid="store-cash-float"
                      />
                    </div>
                    
                    <div>
                      <Label className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Banknote className="w-4 h-4 text-amber-600" />
                          Tolerância Numerário
                        </span>
                        {storeConfigForm.cash_tolerance !== '' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => handleClearOverride('cash_tolerance')}
                          >
                            Usar Global
                          </Button>
                        )}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={`Global: ${formatCurrency(globalConfig.cash_tolerance)}`}
                        value={storeConfigForm.cash_tolerance}
                        onChange={(e) => setStoreConfigForm(prev => ({ ...prev, cash_tolerance: e.target.value }))}
                        className="mt-1"
                        data-testid="store-cash-tolerance"
                      />
                    </div>
                    
                    <div>
                      <Label className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-blue-600" />
                          Tolerância Multibanco
                        </span>
                        {storeConfigForm.card_tolerance !== '' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => handleClearOverride('card_tolerance')}
                          >
                            Usar Global
                          </Button>
                        )}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={`Global: ${formatCurrency(globalConfig.card_tolerance)}`}
                        value={storeConfigForm.card_tolerance}
                        onChange={(e) => setStoreConfigForm(prev => ({ ...prev, card_tolerance: e.target.value }))}
                        className="mt-1"
                        data-testid="store-card-tolerance"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSaveStore} disabled={saving} data-testid="save-store-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'A guardar...' : `Guardar Configuração de ${selectedStore}`}
                    </Button>
                  </div>
                </>
              )}
              
              {!selectedStore && (
                <div className="text-center py-8 text-slate-500">
                  Selecione uma loja para configurar
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Stores overview table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumo de Configurações</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Loja</TableHead>
                    <TableHead>Fundo de Caixa</TableHead>
                    <TableHead>Tol. Numerário</TableHead>
                    <TableHead>Tol. Multibanco</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-slate-50">
                    <TableCell className="font-medium">
                      <span className="text-slate-500">(Global Default)</span>
                    </TableCell>
                    <TableCell>{formatCurrency(globalConfig.default_cash_float)}</TableCell>
                    <TableCell>{formatCurrency(globalConfig.cash_tolerance)}</TableCell>
                    <TableCell>{formatCurrency(globalConfig.card_tolerance)}</TableCell>
                  </TableRow>
                  {stores.map(store => (
                    <StoreConfigRow 
                      key={store} 
                      storeCode={store} 
                      globalConfig={globalConfig}
                      onSelect={() => setSelectedStore(store)}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* WinTouch/SAGE Mapping Tab */}
        <TabsContent value="mapping" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Mapeamento WinTouch → SAGE
                </CardTitle>
                <CardDescription>
                  Correspondência entre códigos WinTouch e armazéns SAGE. Usado para controlo de acesso (RBAC).
                </CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setMappingForm({ name: '', wintouch_store_code: '', sage_warehouse: '', isl_series: '', active: true });
                  setEditingStore(null);
                  setShowAddDialog(true);
                }}
                data-testid="add-mapping-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Loja
              </Button>
            </CardHeader>
            <CardContent>
              {loadingMapping ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : storeMapping.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Store className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhum mapeamento configurado</p>
                  <p className="text-sm mt-2">Clique em "Adicionar Loja" para criar o primeiro mapeamento</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome da Loja</TableHead>
                      <TableHead>Código WinTouch</TableHead>
                      <TableHead>Armazém SAGE</TableHead>
                      <TableHead>Série ISL</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {storeMapping.map((store) => (
                      <TableRow key={store.wintouch_store_code}>
                        <TableCell className="font-medium">{store.name || '—'}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm">
                            {store.wintouch_store_code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                            {store.sage_warehouse}
                          </code>
                        </TableCell>
                        <TableCell>{store.isl_series || '—'}</TableCell>
                        <TableCell>
                          {store.active ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <CheckCircle2 className="w-4 h-4" />
                              Activa
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-400">
                              Inactiva
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMapping(store)}
                              data-testid={`edit-mapping-${store.wintouch_store_code}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteMapping(store.wintouch_store_code)}
                              data-testid={`delete-mapping-${store.wintouch_store_code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              <Alert className="mt-4 bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  O utilizador com <strong>allowed_warehouses</strong> contendo o código SAGE (ex: "012") 
                  terá acesso apenas às lojas WinTouch mapeadas para esse armazém (ex: "NORTESH").
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
          
          {/* Add/Edit Mapping Dialog */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingStore ? 'Editar Mapeamento' : 'Adicionar Mapeamento'}
                </DialogTitle>
                <DialogDescription>
                  {editingStore 
                    ? `Editar configuração de ${editingStore}` 
                    : 'Configure a correspondência entre WinTouch e SAGE'}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="mapping-name">Nome da Loja</Label>
                  <Input
                    id="mapping-name"
                    placeholder="Ex: NorteShopping"
                    value={mappingForm.name}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="mapping-name-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="mapping-wintouch">Código WinTouch</Label>
                  <Input
                    id="mapping-wintouch"
                    placeholder="Ex: NORTESH"
                    value={mappingForm.wintouch_store_code}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, wintouch_store_code: e.target.value.toUpperCase() }))}
                    disabled={!!editingStore}
                    data-testid="mapping-wintouch-input"
                  />
                  {editingStore && (
                    <p className="text-xs text-slate-500">O código WinTouch não pode ser alterado</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="mapping-sage">Armazém SAGE</Label>
                  <Input
                    id="mapping-sage"
                    placeholder="Ex: 012"
                    value={mappingForm.sage_warehouse}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, sage_warehouse: e.target.value }))}
                    data-testid="mapping-sage-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="mapping-isl">Série ISL (opcional)</Label>
                  <Input
                    id="mapping-isl"
                    placeholder="Ex: 5"
                    value={mappingForm.isl_series}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, isl_series: e.target.value }))}
                    data-testid="mapping-isl-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="mapping-active"
                    checked={mappingForm.active}
                    onCheckedChange={(checked) => setMappingForm(prev => ({ ...prev, active: checked }))}
                    data-testid="mapping-active-switch"
                  />
                  <Label htmlFor="mapping-active">Loja Activa</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveMapping} 
                  disabled={savingMapping || !mappingForm.wintouch_store_code || !mappingForm.sage_warehouse}
                  data-testid="save-mapping-btn"
                >
                  {savingMapping ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* SKU Mappings Tab */}
        <TabsContent value="sku-mappings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowRight className="w-5 h-5" />
                    Mapeamentos SKU (WinTouch → SAGE)
                  </CardTitle>
                  <CardDescription>
                    Converter códigos de artigo WinTouch para códigos SAGE quando são diferentes.
                    Usado quando a loja usa um código diferente do catálogo SAGE.
                  </CardDescription>
                </div>
                <Button onClick={handleNewSkuMapping} data-testid="new-sku-mapping-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Mapeamento
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSkuMappings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : skuMappings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>Sem mapeamentos de SKU configurados.</p>
                  <p className="text-sm mt-1">Os mapeamentos permitem converter códigos WinTouch em códigos SAGE.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU WinTouch</TableHead>
                      <TableHead><ArrowRight className="w-4 h-4 inline" /></TableHead>
                      <TableHead>SKU SAGE</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Criado Por</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skuMappings.map((mapping) => (
                      <TableRow 
                        key={mapping.wintouch_sku}
                        className={!mapping.active ? 'opacity-50 bg-slate-50' : ''}
                        data-testid={`sku-mapping-row-${mapping.wintouch_sku}`}
                      >
                        <TableCell className="font-mono font-medium">
                          {mapping.wintouch_sku}
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </TableCell>
                        <TableCell className="font-mono">
                          {mapping.sage_sku}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                          {mapping.description || mapping.wintouch_description || '—'}
                        </TableCell>
                        <TableCell>
                          {mapping.active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              <ToggleRight className="w-3 h-3" />
                              Ativo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                              <ToggleLeft className="w-3 h-3" />
                              Inativo
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {mapping.created_by || '—'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {mapping.created_at 
                            ? new Date(mapping.created_at).toLocaleDateString('pt-PT')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {mapping.active ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditSkuMapping(mapping)}
                                  title="Editar"
                                  data-testid={`edit-sku-mapping-${mapping.wintouch_sku}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeactivateSkuMapping(mapping.wintouch_sku)}
                                  title="Desativar"
                                  className="text-amber-600 hover:text-amber-700"
                                  data-testid={`deactivate-sku-mapping-${mapping.wintouch_sku}`}
                                >
                                  <ToggleLeft className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReactivateSkuMapping(mapping.wintouch_sku)}
                                title="Reativar"
                                className="text-emerald-600 hover:text-emerald-700"
                                data-testid={`reactivate-sku-mapping-${mapping.wintouch_sku}`}
                              >
                                <ToggleRight className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* SKU Mapping Dialog */}
          <Dialog open={showSkuMappingDialog} onOpenChange={setShowSkuMappingDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingSkuMapping ? 'Editar Mapeamento SKU' : 'Novo Mapeamento SKU'}
                </DialogTitle>
                <DialogDescription>
                  Mapear um código de artigo WinTouch para o código correspondente no SAGE.
                </DialogDescription>
              </DialogHeader>
              
              {skuMappingError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{skuMappingError}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-4 py-4">
                {/* WinTouch SKU */}
                <div>
                  <Label htmlFor="wintouch-sku">SKU WinTouch</Label>
                  <Input
                    id="wintouch-sku"
                    placeholder="Código do artigo no WinTouch"
                    value={skuMappingForm.wintouch_sku}
                    onChange={(e) => setSkuMappingForm(prev => ({ 
                      ...prev, 
                      wintouch_sku: e.target.value.toUpperCase() 
                    }))}
                    disabled={!!editingSkuMapping}
                    className="font-mono"
                    data-testid="wintouch-sku-input"
                  />
                  {editingSkuMapping && (
                    <p className="text-xs text-slate-500 mt-1">
                      O SKU WinTouch não pode ser alterado. Crie um novo mapeamento se necessário.
                    </p>
                  )}
                </div>
                
                {/* SAGE SKU with shared SageProductSelector */}
                <SageProductSelector
                  label="Artigo SAGE"
                  placeholder="Pesquisar artigo no catálogo..."
                  value={selectedProduct}
                  onChange={(product) => {
                    setSelectedProduct(product);
                    setSkuMappingForm(prev => ({
                      ...prev,
                      sage_sku: product?.sku || '',
                    }));
                  }}
                  disabled={savingSkuMapping}
                  error={skuMappingError && !selectedProduct ? "Selecione um artigo SAGE válido" : null}
                  data-testid="sage-product-selector-mapping"
                />
                
                {/* Description */}
                <div>
                  <Label htmlFor="mapping-desc">Notas / Motivo</Label>
                  <Input
                    id="mapping-desc"
                    placeholder="Opcional - motivo do mapeamento"
                    value={skuMappingForm.description}
                    onChange={(e) => setSkuMappingForm(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="mapping-description-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSkuMappingDialog(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveSkuMapping} 
                  disabled={savingSkuMapping || !skuMappingForm.wintouch_sku || !selectedProduct}
                  data-testid="save-sku-mapping-btn"
                >
                  {savingSkuMapping ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Store config row component
function StoreConfigRow({ storeCode, globalConfig, onSelect }) {
  const { getStoreName } = useStoreMapping();
  const [config, setConfig] = useState(null);
  
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/retail-closures/config/store/${storeCode}`);
        setConfig(res.data);
      } catch (err) {
        console.error('Error loading store config:', err);
      }
    };
    load();
  }, [storeCode]);
  
  if (!config) {
    return (
      <TableRow>
        <TableCell>{getStoreName(storeCode)}</TableCell>
        <TableCell colSpan={3} className="text-slate-400">A carregar...</TableCell>
      </TableRow>
    );
  }
  
  const isFloatOverride = config.cash_float_retained !== globalConfig.default_cash_float;
  const isCashTolOverride = config.cash_tolerance !== globalConfig.cash_tolerance;
  const isCardTolOverride = config.card_tolerance !== globalConfig.card_tolerance;
  
  return (
    <TableRow className="cursor-pointer hover:bg-slate-50" onClick={onSelect}>
      <TableCell className="font-medium">{getStoreName(storeCode)}</TableCell>
      <TableCell>
        <EffectiveValue value={config.cash_float_retained} isOverride={isFloatOverride} />
      </TableCell>
      <TableCell>
        <EffectiveValue value={config.cash_tolerance} isOverride={isCashTolOverride} />
      </TableCell>
      <TableCell>
        <EffectiveValue value={config.card_tolerance} isOverride={isCardTolOverride} />
      </TableCell>
    </TableRow>
  );
}
